import classNames from "classnames";
import FilmwebLink from 'src/components/filmweblink/FilmwebLink';
import CmsImage from 'src/components/images/CmsImage';
import { CmsImageForUrl } from 'src/components/images/CmsImageForUrl';
import Image from "src/components/images/Image";
import KinoklubbPosterBanner from 'src/kinoklubbposterbanner/KinoklubbPosterBanner';
import { type ImageVersion, type Movie, type StreamingContent, type WatchableContent } from 'src/lib/movieinfo/movieinfotypes';
import { isMovie, isStreamingContent, isWatchableContent } from "src/lib/movieinfo/typeGuards";
import type { IFilmomtale } from 'src/lib/types/Filmomtale';
import { isFilmomtale } from 'src/lib/types/Filmomtale';
import cssPosterStyles from "./CSSPoster.module.css";
import styles from "./PosterImage.module.css";
import { locale } from "@locale";

//#region [Props]
type PosterImageProps = {
  film: IFilmomtale | Movie | StreamingContent | WatchableContent;
  isKinoklubb?: boolean;
  showMovieTitleIfPosterIsMissing?: boolean;
  filmUrl: string;
  hasPoster: boolean;
  sizes?: string;
  width?: number;
  height?: number;
  posterMissingText?: string;
  hideKinoklubbBanner?: boolean;
  imageLoading?: "lazy" | "eager";
  posterOverlay?: React.ReactNode;
  showInNaturalFormat?: boolean;
  posterMissingClassName?: string;
};
//#endregion

//#region [Component]
export default function PosterImage({
  film,
  isKinoklubb = false,
  hasPoster,
  showMovieTitleIfPosterIsMissing = false,
  filmUrl,
  sizes,
  width,
  height,
  posterMissingText = locale.poster.posterMissing,
  hideKinoklubbBanner = false,
  imageLoading,
  posterOverlay,
  showInNaturalFormat,
  posterMissingClassName
}: PosterImageProps) {
  const isUnoptimized = !!width ? true : false;
  return <div className={classNames("PosterImage", cssPosterStyles.CSSPosterWrapper, {
    [cssPosterStyles.CSSPosterMissing]: !hasPoster,
    [posterMissingClassName ?? ""]: !hasPoster,
    [cssPosterStyles.CSSPosterMissingMovieTitle]: !hasPoster && showMovieTitleIfPosterIsMissing
  })} data-postermissingtext={posterMissingText} data-movietitle={film.title}>
        <FilmwebLink to={filmUrl} className={classNames(cssPosterStyles.CSSPosterSizer, styles.SFilmwebLink)} aria-label={!hasPoster ? posterMissingText : undefined}>
            {hasPoster && <>
                {isFilmomtale(film) && <CmsImage alt={`${locale.poster.posterFor} '${film.title}'`} className={styles.CSSImageStaticHeightImage} image={film?.mainPosterV2 ?? film.postersV2?.[0]} sizes={sizes} width={width} unoptimized={isUnoptimized} loading={imageLoading} isPosterAspect={!showInNaturalFormat} />}
                {isMovie(film) && <CmsImageForUrl imageUrl={film.sanityImagePosterUrl!} alt={`${locale.poster.posterFor} '${film.title}'`} className={styles.CSSImageStaticHeightImage} sizes={sizes} width={width} height={height} unoptimized={isUnoptimized} isPosterAspect={!showInNaturalFormat} loading={imageLoading} applyAspectRatio />}
                {isStreamingContent(film) && hasPoster && <>
                    {film.sanityImagePosterUrl && <CmsImageForUrl imageUrl={film.sanityImagePosterUrl} className={styles.CSSImageStaticHeightImage} alt={`${locale.poster.posterFor} '${film.title}'`} sizes={sizes} width={width} unoptimized={isUnoptimized} loading={imageLoading} isPosterAspect={!showInNaturalFormat} />}
                    {!film.sanityImagePosterUrl && <Image image={film.imagesPoster as ImageVersion[]} alt={`${locale.poster.posterFor} '${film.title}'`} className={styles.CSSImageStaticHeightImage} loading={imageLoading} sizes={sizes} />}
                </>}
                {isWatchableContent(film) && hasPoster && <>
                    {film.sanityImagePosterUrl && <CmsImageForUrl imageUrl={film.sanityImagePosterUrl} className={styles.CSSImageStaticHeightImage} alt={`${locale.poster.posterFor} '${film.title}'`} sizes={sizes} width={width} unoptimized={isUnoptimized} loading={imageLoading} isPosterAspect={!showInNaturalFormat} />}
                    {!film.sanityImagePosterUrl && <Image image={film.imagesPosterStreaming as ImageVersion[]} alt={`${locale.poster.posterFor} '${film.title}'`} className={styles.CSSImageStaticHeightImage} loading={imageLoading} sizes={sizes} />}
                </>}
            </>}
            {process.env.NEXT_PUBLIC_KINOKLUBB_ENABLED === "true" && isKinoklubb && !hideKinoklubbBanner && <KinoklubbPosterBanner />}
        </FilmwebLink>
        {posterOverlay}
    </div>;
}
//#endregion